import React, {
    useContext,
    useState,
    useEffect,
    useCallback
} from 'react';
import {
    Switch,
    Route,
    Redirect, useLocation, BrowserRouter as Router
} from 'react-router-dom';
import { subscribe } from '@jutro/events';
import { RoutingTracking } from 'gw-portals-tracking-react';
// Imported First to allow Jutro styles to be overridden
import './App.scss';
import { setComponentMapOverrides } from '@jutro/uiconfig';
import {
    Loader,
    ModalNextProvider
} from '@jutro/components';
import { TranslatorContext, LocaleService } from '@jutro/locale';
import {
    useSgAuthentication,
    logout,
    SgCepHeader,
    SgCepFooter
} from 'saga-cep-specific-components';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import vmTranslator from 'gw-platform-translations';
import { AccurateBreakpointPropagation } from 'gw-jutro-adapters-react';
import { WizardPageTemplateWithTitle } from 'gw-portals-wizard-react';
import { DependencyProvider } from 'gw-portals-dependency-react';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { LogoutWarningModal } from 'gw-digital-auth-react';
import { ViewModelServiceFactory } from 'gw-portals-viewmodel-js';
import { EndorsementWizard } from 'sg-capability-endorsement-react';
import { RenewalWizard } from 'sg-capability-renewal-react';
import {
    platformComponentMap,
    platformComponents,
    ImageComponent,
} from 'gw-components-platform-react';
import { ErrorBoundary } from 'gw-portals-error-react';
import { SgErrorPage, IpidPage } from 'saga-common-pages';
import {
    SgVehicleValidator,
    SgAdobeTargetOfferService,
    SgAdobeMVTService,
    SgActiveTestsContext,
    AdobeAnalyticsProvider,
    CepAdobeAnalyticsAdapter,
    CEP_TAGGING_PROPERTIES,
    SgLoadingContext,
    SgWizardContext
} from 'saga-modules';
import SgSkipNavigationLink from 'sg-skip-navigation-link';
import SgIntercomWebchat from 'sg-intercom-webchat';
import AccountPage from '../customer/Pages/AccountPage/AccountPage';
import LandingPage from '../customer/Pages/LandingPage/LandingPage';
import PaymentPage from '../customer/Pages/PaymentPage/PaymentPage';
import ClaimsPage from '../customer/Pages/ClaimsPage/ClaimsPage';
import FaqPage from '../customer/Pages/FaqPage/FaqPage';
import DocumentsPage from '../customer/Pages/DocumentsPage/DocumentsPage';
import './App.messages';

const {
    inactivityIntervalMins,
    logoutConfirmationIntervalMins
} = appConfig.sessionConfig;

setComponentMapOverrides(
    {
        ...platformComponentMap,
        WizardPageTemplateWithTitle: { component: 'WizardPageTemplateWithTitle' },
        // replace the native IMG component with a proxied version
        img: { component: 'img' },
    },
    {
        ...platformComponents,
        WizardPageTemplateWithTitle,
        img: ImageComponent,
    }
);

function App() {
    const translator = useContext(TranslatorContext);
    const [viewModelService, setViewModelService] = useState(undefined);
    const [showLoadingModal, setShowLoadingModal] = useState(false);

    useEffect(() => {
        const translatorFn = vmTranslator(translator);
        import(
            /* webpackChunkName: "product-metadata" */
            // eslint-disable-next-line import/no-unresolved
            'product-metadata'
        ).then((productMetadata) => {
            const { default: result } = productMetadata;
            setViewModelService(
                ViewModelServiceFactory.getViewModelService(result, translatorFn, 'edgev11')
            );
        });
        LocaleService.setCurrentLocale('en-GB');

        AdobeAnalyticsProvider.configure(
            CepAdobeAnalyticsAdapter,
            CEP_TAGGING_PROPERTIES,
            false
        );

        SgAdobeTargetOfferService.initTargetCapability();
    }, [translator]);

    useEffect(() => {
        if (viewModelService) {
            SgVehicleValidator.init(viewModelService);
        }
    }, [viewModelService]);
    const loadingContext = {
        showLoadingModal,
        setShowLoadingModal
    };

    const [activeMvtTests, setActiveMvtTests] = useState({ activeMvtTests: SgAdobeMVTService.getActiveTests() });
    subscribe('updateActiveMvtTests', (updatedMvtTest) => {
        setActiveMvtTests({ activeMvtTests: updatedMvtTest });
    });

    const [currentWizardStep, setCurrentWizardStep] = useState('start');

    const wizardStepContext = {
        currentWizardStep,
        setCurrentWizardStep
    };

    const { pathname } = useLocation();

    const handleError = useCallback((error = {}) => {
        const state = {
            pathname: pathname,
            hash: '/error',
            state: error
        };

        return (
            <Redirect to={state} />
        );
    }, [pathname]);

    const { isAuthenticated, isError } = useSgAuthentication(!!viewModelService);

    const basename = `${useLocation().pathname}#`;

    if (!isAuthenticated && !isError) {
        return <Loader />;
    }

    return (
        <AccurateBreakpointPropagation>
            <DependencyProvider
                value={{}}
            >
                <ErrorBoundary onError={handleError}>
                    <SgLoadingContext.Provider value={loadingContext}>
                        <ViewModelServiceContext.Provider value={viewModelService}>
                            <Router basename={basename}>
                                <SgWizardContext.Provider value={wizardStepContext}>
                                    <SgSkipNavigationLink isCEP />
                                    <ModalNextProvider />
                                    <LogoutWarningModal
                                        onEndSession={logout}
                                        modalAutoCloseTimeout={logoutConfirmationIntervalMins}
                                        userInactivityTimeout={inactivityIntervalMins}
                                    />
                                    <RoutingTracking />
                                    <SgCepHeader />
                                    <SgIntercomWebchat />
                                    <SgActiveTestsContext.Provider value={activeMvtTests}>
                                        <div id="cepContent" className="qnbContent">
                                            <Switch>
                                                <Route path="/error" component={SgErrorPage} />
                                                <Route exact path="/home" component={LandingPage} />
                                                <Route exact path="/endorsement" component={EndorsementWizard} />
                                                <Route path="/endorsement" component={EndorsementWizard} />
                                                <Route exact path="/billing-summary/:policyNumber?/" component={PaymentPage} />
                                                <Route exact path="/renewal" component={RenewalWizard} />
                                                <Route path="/renewal" component={RenewalWizard} />
                                                <Route exact path="/documents" component={DocumentsPage} />
                                                <Route exact path="/claim" component={ClaimsPage} />
                                                <Route exact path="/faq" component={FaqPage} />
                                                <Route exact path="/ampuserprofile" component={AccountPage} />
                                                <Route path="/IPID" component={IpidPage} />
                                                <Redirect to="/home" />
                                            </Switch>
                                        </div>
                                    </SgActiveTestsContext.Provider>
                                    <SgCepFooter />
                                </SgWizardContext.Provider>
                            </Router>
                        </ViewModelServiceContext.Provider>
                    </SgLoadingContext.Provider>
                </ErrorBoundary>
            </DependencyProvider>
        </AccurateBreakpointPropagation>
    );
}

export default App;
